import { motion } from "framer-motion";
import DOMPurify from "dompurify";
import {
  type RuntimePropsWithChildren,
  SECTION_REVEAL_PROPS,
  ThemeSectionContainer,
  isVisible,
  type BorderSide,
} from "./shared";

/**
 * SECURITY: the Embed section renders admin-authored raw HTML to VISITORS —
 * a stored-XSS surface. The editor sanitizes at save time with the SAME
 * config; this render-time pass is defense-in-depth (old blobs, direct DB
 * writes). Keep this config in sync with main-frontend
 * `src/components/WebsiteBuilder/sections/Embed/embedSanitize.ts`.
 * No scripts, no event handlers (DOMPurify default) and explicitly NO
 * iframes/forms/media-with-src in v1.
 */
export function sanitizeEmbedHtml(value: string): string {
  return DOMPurify.sanitize(value, {
    USE_PROFILES: { html: true },
    FORBID_TAGS: ["iframe", "form", "input", "textarea", "select", "button", "style", "link", "meta", "base", "object", "embed"],
    FORBID_ATTR: ["srcset", "formaction"],
  });
}

/** Custom HTML/embed section — renders the sanitized admin-authored markup. */
export function Embed(props: RuntimePropsWithChildren) {
  if (!isVisible(props.isVisible as boolean | undefined)) return null;
  const html = (props.html as string | undefined) ?? "";
  const padding =
    (props.padding as string | undefined) ?? "p-4 wb-md:p-6 wb-lg:p-8";
  const sectionMargin = (props.sectionMargin as string | undefined) ?? "py-0";

  return (
    <ThemeSectionContainer
      sectionHeight={props.sectionHeight as number | undefined}
      contentAlignment={
        props.contentAlignment as "top" | "center" | "bottom" | undefined
      }
      backgroundColor={props.backgroundColor as string | undefined}
      padding={padding}
      sectionMargin={sectionMargin}
      border={props.border as boolean | undefined}
      borderColor={props.borderColor as string | undefined}
      borderWidth={props.borderWidth as string | undefined}
      borderStyle={props.borderStyle as string | undefined}
      borderSides={props.borderSides as BorderSide[] | undefined}
      borderRadius={props.borderRadius as string | undefined}
    >
      <motion.div
        {...SECTION_REVEAL_PROPS}
        className="w-full max-w-275"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: sanitizeEmbedHtml(html) }}
      />
    </ThemeSectionContainer>
  );
}
